import React from 'react';
import OrganizationSearcher from '../OrganizationSearcher';

const PREVIOUS_ORGANIZATION_STORAGE_KEY_PREFIX = 'rid.previousSelectedOrganization';

const getPreviousOrganizationStorageKey = (currentUser: any) => (
    currentUser?.id
        ? `${PREVIOUS_ORGANIZATION_STORAGE_KEY_PREFIX}.${currentUser.id}`
        : `${PREVIOUS_ORGANIZATION_STORAGE_KEY_PREFIX}.anonymous`
);

const loadPreviousOrganization = (storageKey: string) => {
    try {
        const storedOrganization = window.localStorage.getItem(storageKey);
        return storedOrganization ? JSON.parse(storedOrganization) : null;
    } catch (error) {
        console.error('Failed to load previous organization:', error);
        return null;
    }
};

type TopbarRightProps = {
    onStartPrint: () => void;
    org: any;
    showOrgMenu: boolean;
    setShowOrgMenu: (value: boolean | ((prev: boolean) => boolean)) => void;
    orgDropdownRef: React.RefObject<HTMLDivElement>;
    changeOrganization: (org: any) => void | Promise<void>;
    currentUser: any;
    showUserMenu: boolean;
    setShowUserMenu: (value: boolean | ((prev: boolean) => boolean)) => void;
    userMenuRef: React.RefObject<HTMLDivElement>;
    onEditProfile: () => void;
    onLogout: () => void;
};

export default function TopbarRight({
    onStartPrint,
    org,
    showOrgMenu,
    setShowOrgMenu,
    orgDropdownRef,
    changeOrganization,
    currentUser,
    showUserMenu,
    setShowUserMenu,
    userMenuRef,
    onEditProfile,
    onLogout
}: TopbarRightProps) {
    const previousOrganizationStorageKey = getPreviousOrganizationStorageKey(currentUser);
    const [previousOrganization, setPreviousOrganization] = React.useState<any | null>(() =>
        loadPreviousOrganization(previousOrganizationStorageKey)
    );

    React.useEffect(() => {
        setPreviousOrganization(loadPreviousOrganization(previousOrganizationStorageKey));
    }, [previousOrganizationStorageKey]);

    const handleChangeOrganization = async (nextOrg: any) => {
        const previousOrg = org?.id && String(org.id) !== String(nextOrg?.id)
            ? {
                id: org.id,
                name: org.name,
                active: org.active ?? true,
                marketIds: org.marketIds ?? [],
            }
            : null;

        await changeOrganization(nextOrg);

        if (previousOrg) {
            setPreviousOrganization(previousOrg);
            try {
                window.localStorage.setItem(previousOrganizationStorageKey, JSON.stringify(previousOrg));
            } catch (error) {
                console.error('Failed to store previous organization:', error);
            }
        }
    };

    return (
        <div className="topbar-right">
            {(currentUser?.roleActions?.indexOf('WritePrintRuns') >= 0 ||
                currentUser?.roleActions?.indexOf('AdminPrintRuns') >= 0) && <button data-testid="topbar-start-print" className="button batch" onClick={onStartPrint}>
                <span className="plus" aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3.33301 8H12.6663" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round"
                            strokeLinejoin="round" />
                        <path d="M8 3.33301V12.6663" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round"
                            strokeLinejoin="round" />
                    </svg>
                </span>
                Start Print
            </button>}
            <div className="org-dropdown" data-testid="topbar-organization-selector" role="listbox" aria-label="Select organization" ref={orgDropdownRef}>
                <div
                    className="org-display"
                    data-testid="topbar-organization-toggle"
                    role="button"
                    tabIndex={0}
                    aria-expanded={showOrgMenu}
                    onClick={() => setShowOrgMenu((v) => !v)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            setShowOrgMenu((v) => !v);
                        }
                    }}
                >
                    <span className="org-divider" aria-hidden="true" />
                    <span className="org-name" data-testid="topbar-organization-name">{org?.name || 'Select organization'}</span>
                    <span className="org-divider" aria-hidden="true" />
                    <span className="caret">▾</span>
                </div>
                {showOrgMenu && (
                    <OrganizationSearcher
                        changeOrganization={handleChangeOrganization}
                        organizationId={org?.id||0}
                        setShowOrgMenu={setShowOrgMenu}
                        isSuperUser={currentUser?.isSuperUser ?? false}
                        defaultOrganizationId={currentUser?.defaultOrganizationId ?? null}
                        defaultOrganizationName={currentUser?.org ?? null}
                        previousOrganization={previousOrganization}
                    />
                )}
            </div>
            <div className="user-menu-wrapper" ref={userMenuRef}>
                <button
                    className="avatar"
                    data-testid="topbar-user-menu-toggle"
                    title={`Signed in as ${currentUser?.name || 'User'}`}
                    onClick={() => setShowUserMenu((v) => !v)}
                    aria-haspopup="menu"
                    aria-expanded={showUserMenu}
                >
                    {(currentUser?.name || 'User').split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
                </button>
                {showUserMenu && (
                    <div className="user-dropdown" data-testid="topbar-user-menu" role="menu">
                        <div className="user-dropdown-header">{currentUser?.name || 'User'}</div>
                        <button className="user-dropdown-item" data-testid="topbar-edit-profile" role="menuitem" onClick={onEditProfile}>Edit Profile</button>
                        <button className="user-dropdown-item" data-testid="topbar-logout" role="menuitem" onClick={onLogout}>Logout</button>
                    </div>
                )}
            </div>
        </div>
    );
}
